import {
  compact,
  concat,
  difference,
  first,
  groupBy,
  keys,
  map,
  transform,
  values,
} from 'lodash';

import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  ClientProfessionalTeamForm,
  ClientProfessionalTeamMemberRoleKey,
  ClientProfessionalTeamRole,
  ClientProfessionalTeamRoleMap,
  ClientProfessionalTeamRoleMember,
  getEmptyRoleForKind,
} from '@/modules/professionalTeam/ClientProfessionalTeam.types';
import {
  AugmentedCreateProfessionalTeamRoleInput,
  AugmentedUpdateClientOrganizationInput,
  AugmentedUpdateClientProfileInput,
  AugmentedUpdateProfessionalTeamRoleInput,
  ProfessionalTeamRoleKind,
  UpdateProfessionalTeamRoleInput,
} from '@/types/schema';
import { diagnostics } from '@/utils/diagnostics';
import { getNodes } from '@/utils/graphqlUtils';
import { getPulidKind, PulidKind } from '@/utils/pulid';

import {
  getEmptyAdvisorClientRoleMap,
  getEmptyPrincipalClientRoleMap,
} from './ClientProfessionalTeam.constants';
import {
  ClientProfessionalTeam_ClientOrganizationFragment,
  ClientProfessionalTeam_ClientProfileFragment,
  ClientProfessionalTeam_ProfessionalTeamRoleFragment,
  ClientProfessionalTeamQuery,
} from './graphql/ClientProfessionalTeam.generated';

// Helper to identify organization IDs
function isOrganizationId(id: string): boolean {
  return getPulidKind(id) === PulidKind.ClientOrganization;
}

export function getClientProfessionalTeamRoleKey({
  kind,
  otherRoleName,
}: ClientProfessionalTeam_ProfessionalTeamRoleFragment): ClientProfessionalTeamMemberRoleKey {
  if (kind === ProfessionalTeamRoleKind.Other) {
    return `OTHER-${otherRoleName}`;
  }
  return kind;
}

function getTeamMembers(data: ClientProfessionalTeamQuery | undefined) {
  const individuals = getNodes(data?.clientProfiles);
  const organizations = getNodes(data?.clientOrganizations);
  return [...individuals, ...organizations].filter(({ id }) => !!id);
}

export function mapDataToTeamMemberOptions(
  data: ClientProfessionalTeamQuery | undefined
): SelectInputOption<string>[] {
  const teamMembers = getTeamMembers(data);

  return teamMembers
    .map<SelectInputOption<string>>((teamMember) => {
      const isOrg = isOrganizationId(teamMember.id);
      const displayName = isOrg
        ? (teamMember as ClientProfessionalTeam_ClientOrganizationFragment).name
        : (teamMember as ClientProfessionalTeam_ClientProfileFragment)
            .legalName;

      return {
        value: teamMember.id,
        display: displayName,
      };
    })
    .sort((a, b) => a.display.localeCompare(b.display));
}

function getPrincipalClients(data: ClientProfessionalTeamQuery | undefined) {
  const household = first(getNodes(data?.households));
  return household?.principalClients?.filter(({ id }) => !!id) || [];
}

export function mapDataToPrincipalClientDisplay(
  data: ClientProfessionalTeamQuery | undefined
): Record<string, string> {
  return getPrincipalClients(data).reduce<Record<string, string>>(
    (acc, principalClient) => {
      acc[principalClient.id] = principalClient.displayName;
      return acc;
    },
    {}
  );
}

function addToRole(
  formRole: ClientProfessionalTeamRole | null | undefined,
  fetchedRole: ClientProfessionalTeam_ProfessionalTeamRoleFragment,
  memberId: string
): ClientProfessionalTeamRole {
  const role = formRole || getEmptyRoleForKind(fetchedRole.kind);

  const member: ClientProfessionalTeamRoleMember = {
    roleId: fetchedRole.id,
    memberId,
    powerOfAttorneyKind: fetchedRole.powerOfAttorneyKind || '',
  };

  // check the memberId instead of the list length, as an empty list has a
  // single element with a blank memberId
  if (!role.members[0]?.memberId) {
    role.members = [member];
  } else {
    role.members.push(member);
  }

  role.otherRoleName = role?.otherRoleName || fetchedRole?.otherRoleName || '';

  return role;
}

export function mapDataToColumn(
  data: ClientProfessionalTeamQuery | undefined
): ClientProfessionalTeamForm {
  const advisorClientRoles: ClientProfessionalTeamForm['advisorClientRoles'] =
    getEmptyAdvisorClientRoleMap();

  const principalClientIds = map(getPrincipalClients(data), ({ id }) => id);

  const principalClientIdTeamMemberMap: ClientProfessionalTeamForm['principalClientIdTeamMemberMap'] =
    principalClientIds.reduce<
      ClientProfessionalTeamForm['principalClientIdTeamMemberMap']
    >((acc, principalClientId) => {
      acc[principalClientId] = getEmptyPrincipalClientRoleMap();
      return acc;
    }, {});

  const professionalTeamMembers = compact(
    getTeamMembers(data).filter(
      (professionalTeamMember) => !!professionalTeamMember.id
    )
  );

  professionalTeamMembers.forEach((teamMember) => {
    const roles = getNodes(teamMember.professionalTeamRoles);
    roles.forEach((role) => {
      const roleKey = getClientProfessionalTeamRoleKey(role);

      const principalClientId = role.associatedClientIndividual?.id ?? '';
      const principalClientRoles =
        principalClientIdTeamMemberMap[principalClientId];

      if (principalClientId) {
        if (!principalClientRoles) {
          diagnostics.error('Attempted to set role on non-principal client');
          return;
        }

        principalClientRoles[roleKey] = addToRole(
          principalClientRoles[roleKey],
          role,
          teamMember.id
        );
      } else if (role.household) {
        advisorClientRoles[roleKey] = addToRole(
          advisorClientRoles[roleKey],
          role,
          teamMember.id
        );
      }
    });
  });

  return {
    advisorClientRoles,
    principalClientIdTeamMemberMap,
  };
}

interface UserRole
  extends Omit<ClientProfessionalTeamRole, 'members'>,
    ClientProfessionalTeamRoleMember {
  principalClientId?: string;
}

function validateOtherRoleName({ kind, otherRoleName }: UserRole): void {
  if (kind !== ProfessionalTeamRoleKind.Other) {
    return;
  }

  if (!otherRoleName) {
    throw new Error(
      'A descriptive name is required if the role type is "Other"'
    );
  }

  return;
}

function flattenClientProfessionalTeamRoleMap(
  roleMap: Partial<ClientProfessionalTeamRoleMap> | null | undefined,
  principalClientId?: string
): UserRole[] {
  return values(roleMap).reduce<UserRole[]>((acc, mappedValue) => {
    const roleMembers: UserRole[] =
      mappedValue?.members.map<UserRole>((member) => {
        return {
          ...mappedValue,
          ...member,
          principalClientId,
        };
      }) || [];
    return acc.concat(roleMembers);
  }, []);
}

function buildClientProfileUpdates(
  roleUpdates: UserRole[],
  queryData: ClientProfessionalTeamQuery | undefined,
  householdId: string
): AugmentedUpdateClientProfileInput[] {
  const teamMembers = getNodes(queryData?.clientProfiles).filter(
    ({ id }) => !!id
  );

  // step 2: group the role updates per pro team member
  const proTeamMemberIdsToRoles = groupBy(roleUpdates, 'memberId');

  // step 3: map from user-centric data to proper output format
  const updates: AugmentedUpdateClientProfileInput[] = transform<
    UserRole[],
    AugmentedUpdateClientProfileInput[]
  >(
    proTeamMemberIdsToRoles,
    (acc, proTeamMemberRoles, proTeamMemberId) => {
      const initialTeamRoles = getNodes(
        teamMembers.find((teamMember) => teamMember.id === proTeamMemberId)
          ?.professionalTeamRoles
      );
      const initialTeamRoleIds: string[] = initialTeamRoles?.map(
        (teamRole) => teamRole.id
      );

      const addedTeamRoles = proTeamMemberRoles.filter(({ roleId }) => !roleId);

      const currentTeamRoleIds: string[] = compact(
        proTeamMemberRoles.map(({ roleId }) => roleId || null)
      );

      const removeProfessionalTeamRoleIDs = difference(
        initialTeamRoleIds,
        currentTeamRoleIds
      );

      const updatedTeamRoles = proTeamMemberRoles.filter(
        ({ roleId }) =>
          // role is updated if has a roleId (not existing) and not in the removed list
          !!roleId && !removeProfessionalTeamRoleIDs.includes(roleId)
      );

      const augmentedClientProfileInput: AugmentedUpdateClientProfileInput = {
        id: proTeamMemberId,
        update: {
          removeProfessionalTeamRoleIDs: removeProfessionalTeamRoleIDs.length
            ? removeProfessionalTeamRoleIDs
            : undefined,
        },
        withProfessionalTeamRoles:
          addedTeamRoles.map<AugmentedCreateProfessionalTeamRoleInput>(
            (addedRole) => {
              validateOtherRoleName(addedRole);
              return {
                create: {
                  associatedClientIndividualID:
                    addedRole.principalClientId || undefined,
                  kind: addedRole.kind,
                  otherRoleName: addedRole.otherRoleName || undefined,
                  householdID: householdId,
                  powerOfAttorneyKind:
                    addedRole.powerOfAttorneyKind || undefined,
                },
              };
            }
          ),
        updateProfessionalTeamRoles:
          updatedTeamRoles.map<AugmentedUpdateProfessionalTeamRoleInput>(
            (updatedRole) => {
              if (!updatedRole.roleId) {
                throw new Error('Cannot update role without ID');
              }
              validateOtherRoleName(updatedRole);

              let clearOtherRoleName: UpdateProfessionalTeamRoleInput['clearOtherRoleName'] =
                undefined;
              if (updatedRole.kind === ProfessionalTeamRoleKind.Other) {
                clearOtherRoleName = false;
              }

              let clearPowerOfAttorneyKind: UpdateProfessionalTeamRoleInput['clearPowerOfAttorneyKind'] =
                true;
              if (
                updatedRole.kind === ProfessionalTeamRoleKind.PowerOfAttorney
              ) {
                clearPowerOfAttorneyKind = !updatedRole.powerOfAttorneyKind;
              }

              return {
                id: updatedRole.roleId,
                update: {
                  associatedClientIndividualID:
                    updatedRole.principalClientId || undefined,
                  clearOtherRoleName,
                  clearPowerOfAttorneyKind,
                  kind: updatedRole.kind,
                  otherRoleName: updatedRole.otherRoleName || undefined,
                  powerOfAttorneyKind:
                    updatedRole.powerOfAttorneyKind || undefined,
                },
              };
            }
          ),
      };
      acc.push(augmentedClientProfileInput);
      return acc;
    },
    []
  );

  return updates;
}

function buildClientOrganizationUpdates(
  roleUpdates: UserRole[],
  queryData: ClientProfessionalTeamQuery | undefined,
  householdId: string
): AugmentedUpdateClientOrganizationInput[] {
  const teamMembers = getNodes(queryData?.clientOrganizations).filter(
    ({ id }) => !!id
  );

  // step 2: group the role updates per pro team member
  const proTeamMemberIdsToRoles = groupBy(roleUpdates, 'memberId');

  // step 3: map from user-centric data to proper output format
  const updates: AugmentedUpdateClientOrganizationInput[] = transform<
    UserRole[],
    AugmentedUpdateClientOrganizationInput[]
  >(
    proTeamMemberIdsToRoles,
    (acc, proTeamMemberRoles, proTeamMemberId) => {
      const initialTeamRoles = getNodes(
        teamMembers.find((teamMember) => teamMember.id === proTeamMemberId)
          ?.professionalTeamRoles
      );
      const initialTeamRoleIds: string[] = initialTeamRoles?.map(
        (teamRole) => teamRole.id
      );

      const addedTeamRoles = proTeamMemberRoles.filter(({ roleId }) => !roleId);

      const currentTeamRoleIds: string[] = compact(
        proTeamMemberRoles.map(({ roleId }) => roleId || null)
      );

      const removeProfessionalTeamRoleIDs = difference(
        initialTeamRoleIds,
        currentTeamRoleIds
      );

      const updatedTeamRoles = proTeamMemberRoles.filter(
        ({ roleId }) =>
          // role is updated if has a roleId (not existing) and not in the removed list
          !!roleId && !removeProfessionalTeamRoleIDs.includes(roleId)
      );

      const augmentedClientOrganizationInput: AugmentedUpdateClientOrganizationInput =
        {
          id: proTeamMemberId,
          update: {
            removeProfessionalTeamRoleIDs: removeProfessionalTeamRoleIDs.length
              ? removeProfessionalTeamRoleIDs
              : undefined,
          },
          withProfessionalTeamRoles:
            addedTeamRoles.map<AugmentedCreateProfessionalTeamRoleInput>(
              (addedRole) => {
                validateOtherRoleName(addedRole);
                return {
                  create: {
                    associatedClientIndividualID:
                      addedRole.principalClientId || undefined,
                    kind: addedRole.kind,
                    otherRoleName: addedRole.otherRoleName || undefined,
                    householdID: householdId,
                    powerOfAttorneyKind:
                      addedRole.powerOfAttorneyKind || undefined,
                  },
                };
              }
            ),
          updateProfessionalTeamRoles:
            updatedTeamRoles.map<AugmentedUpdateProfessionalTeamRoleInput>(
              (updatedRole) => {
                if (!updatedRole.roleId) {
                  throw new Error('Cannot update role without ID');
                }
                validateOtherRoleName(updatedRole);

                let clearOtherRoleName: UpdateProfessionalTeamRoleInput['clearOtherRoleName'] =
                  undefined;
                if (updatedRole.kind === ProfessionalTeamRoleKind.Other) {
                  clearOtherRoleName = false;
                }

                let clearPowerOfAttorneyKind: UpdateProfessionalTeamRoleInput['clearPowerOfAttorneyKind'] =
                  true;
                if (
                  updatedRole.kind === ProfessionalTeamRoleKind.PowerOfAttorney
                ) {
                  clearPowerOfAttorneyKind = !updatedRole.powerOfAttorneyKind;
                }

                return {
                  id: updatedRole.roleId,
                  update: {
                    associatedClientIndividualID:
                      updatedRole.principalClientId || undefined,
                    clearOtherRoleName,
                    clearPowerOfAttorneyKind,
                    kind: updatedRole.kind,
                    otherRoleName: updatedRole.otherRoleName || undefined,
                    powerOfAttorneyKind:
                      updatedRole.powerOfAttorneyKind || undefined,
                  },
                };
              }
            ),
        };
      acc.push(augmentedClientOrganizationInput);
      return acc;
    },
    []
  );

  return updates;
}

export interface SplitUpdates {
  clientProfileUpdates: AugmentedUpdateClientProfileInput[];
  clientOrganizationUpdates: AugmentedUpdateClientOrganizationInput[];
}

// Build a map of roleId → original owner memberId from query data.
// Used to detect when a role has been reassigned to a different member.
function buildRoleIdToOwnerMap(
  queryData: ClientProfessionalTeamQuery | undefined
): Record<string, string> {
  const roleIdToOwner: Record<string, string> = {};
  const teamMembers = getTeamMembers(queryData);
  for (const member of teamMembers) {
    const roles = getNodes(member.professionalTeamRoles);
    for (const role of roles) {
      roleIdToOwner[role.id] = member.id;
    }
  }
  return roleIdToOwner;
}

// Inject role removals for original owners whose roles were reassigned to a
// different member or removed entirely. Mutates the update arrays in place.
function injectReassignmentRemovals(
  removalsForOriginalOwners: Record<string, string[]>,
  clientProfileUpdates: AugmentedUpdateClientProfileInput[],
  clientOrganizationUpdates: AugmentedUpdateClientOrganizationInput[]
): void {
  for (const [ownerId, roleIds] of Object.entries(removalsForOriginalOwners)) {
    if (isOrganizationId(ownerId)) {
      const existing = clientOrganizationUpdates.find((u) => u.id === ownerId);
      if (existing) {
        const currentRemovals =
          existing.update.removeProfessionalTeamRoleIDs || [];
        existing.update.removeProfessionalTeamRoleIDs = [
          ...currentRemovals,
          ...roleIds,
        ];
      } else {
        clientOrganizationUpdates.push({
          id: ownerId,
          update: { removeProfessionalTeamRoleIDs: roleIds },
        });
      }
    } else {
      const existing = clientProfileUpdates.find((u) => u.id === ownerId);
      if (existing) {
        const currentRemovals =
          existing.update.removeProfessionalTeamRoleIDs || [];
        existing.update.removeProfessionalTeamRoleIDs = [
          ...currentRemovals,
          ...roleIds,
        ];
      } else {
        clientProfileUpdates.push({
          id: ownerId,
          update: { removeProfessionalTeamRoleIDs: roleIds },
        });
      }
    }
  }
}

export function mapFormDataToAugmentedUpdates(
  formData: ClientProfessionalTeamForm,
  queryData: ClientProfessionalTeamQuery | undefined,
  householdId: string
): SplitUpdates {
  const principalUserIds = keys(formData.principalClientIdTeamMemberMap);

  // Build a map of roleId → original owner so we can detect reassignments
  const roleIdToOriginalOwner = buildRoleIdToOwnerMap(queryData);

  // step 1: flatten & merge data into an array of all role updates
  const allRoleUpdates = concat<UserRole>(
    flattenClientProfessionalTeamRoleMap(formData.advisorClientRoles),
    ...principalUserIds.map((principalClientId) =>
      flattenClientProfessionalTeamRoleMap(
        formData.principalClientIdTeamMemberMap[principalClientId],
        principalClientId
      )
    )
  ).filter((update) => !!update.memberId); // and filter out all the "None" choices

  // step 1.5: detect reassigned roles — when a roleId's original owner differs
  // from the current memberId, the role was moved to a different person.
  // The backend ProfessionalTeamRole edges are immutable, so we must delete the
  // old role and create a new one on the new member.
  const removalsForOriginalOwners: Record<string, string[]> = {};
  for (const update of allRoleUpdates) {
    if (!update.roleId) continue;
    const originalOwner = roleIdToOriginalOwner[update.roleId];
    if (originalOwner && originalOwner !== update.memberId) {
      const reassignedRoleId = update.roleId;
      // Clear roleId so downstream logic treats this as a new role creation
      update.roleId = '';
      // Track the old roleId for removal from original owner
      if (!removalsForOriginalOwners[originalOwner]) {
        removalsForOriginalOwners[originalOwner] = [];
      }
      removalsForOriginalOwners[originalOwner].push(reassignedRoleId);
    }
  }

  // step 1.6: detect roles that were removed entirely. The per-member removal
  // logic in buildClient*Updates only runs for members who still have at least
  // one role in the submitted form, so a member whose every role was removed
  // (e.g. deleting a CPA who held no other role) is never visited and their
  // orphaned role is never deleted. Queue those removals against their owner.
  const submittedRoleIds = new Set(
    compact(allRoleUpdates.map(({ roleId }) => roleId))
  );
  const ownersWithSubmittedRoles = new Set(
    allRoleUpdates.map(({ memberId }) => memberId)
  );
  for (const [roleId, owner] of Object.entries(roleIdToOriginalOwner)) {
    // still present in the form → keep it
    if (submittedRoleIds.has(roleId)) continue;
    // owner still has roles in the form → buildClient*Updates handles removal
    if (ownersWithSubmittedRoles.has(owner)) continue;
    if (!removalsForOriginalOwners[owner]) {
      removalsForOriginalOwners[owner] = [];
    }
    // dedupe vs. the reassignment loop above
    if (!removalsForOriginalOwners[owner].includes(roleId)) {
      removalsForOriginalOwners[owner].push(roleId);
    }
  }

  // step 2: split role updates by entity type
  const profileRoleUpdates = allRoleUpdates.filter(
    (update) => !isOrganizationId(update.memberId)
  );
  const organizationRoleUpdates = allRoleUpdates.filter((update) =>
    isOrganizationId(update.memberId)
  );

  // step 3: build client profile updates
  const clientProfileUpdates = buildClientProfileUpdates(
    profileRoleUpdates,
    queryData,
    householdId
  );

  // step 4: build client organization updates
  const clientOrganizationUpdates = buildClientOrganizationUpdates(
    organizationRoleUpdates,
    queryData,
    householdId
  );

  // step 5: inject removals for original owners of reassigned roles
  injectReassignmentRemovals(
    removalsForOriginalOwners,
    clientProfileUpdates,
    clientOrganizationUpdates
  );

  return {
    clientProfileUpdates,
    clientOrganizationUpdates,
  };
}
